const TEXT_LIKE_QUESTION_TYPES = ["TEXT_AREA", "TEXT_BOX"] as const;

export enum AnswerWriteMode {
  Text = "text",
  Options = "options",
}

export type ValueTypedUpdate =
  | { kind: "leave" }
  | { kind: "set"; value: string | null };

export type AnswerUpsertPlan =
  | {
      kind: "no-op";
      willDeleteChildren: boolean;
    }
  | {
      kind: "create";
      willDeleteChildren: boolean;
    }
  | {
      kind: "delete";
      willDeleteChildren: boolean;
    }
  | {
      kind: "update";
      willDeleteChildren: boolean;
      normalizedOptionIds: string[];
      removedOptionIds: string[];
      addedOptionIds: string[];
      valueTypedUpdate: ValueTypedUpdate;
    };

export function computeAnswerUpsertPlan(params: {
  questionType: string | null | undefined;
  hasExistingAnswer: boolean;
  currentOptionIds: readonly string[];
  nextValueTyped: string | null | undefined;
  nextOptionIds: readonly string[] | null | undefined;
  forceCleanSlate: boolean;
}): AnswerUpsertPlan {
  const {
    questionType,
    hasExistingAnswer,
    currentOptionIds,
    nextValueTyped,
    nextOptionIds,
    forceCleanSlate,
  } = params;

  const { normalizedValueTyped } = normalizeValueTyped(nextValueTyped);
  const normalizedOptionIds = dedupePreserveOrder(nextOptionIds ?? []);

  if (!hasExistingAnswer) {
    if (normalizedValueTyped === null && normalizedOptionIds.length === 0) {
      return { kind: "no-op", willDeleteChildren: forceCleanSlate };
    }

    return {
      kind: "create",
      willDeleteChildren: forceCleanSlate,
    };
  }

  const mode: AnswerWriteMode = isTextLikeQuestionType(questionType)
    ? AnswerWriteMode.Text
    : AnswerWriteMode.Options;

  if (mode === AnswerWriteMode.Text) {
    const willDeleteSelf =
      normalizedValueTyped === null ||
      (normalizedValueTyped.length === 0 && currentOptionIds.length === 0);

    if (willDeleteSelf) {
      return {
        kind: "delete",
        willDeleteChildren: true,
      };
    }

    return {
      kind: "update",
      willDeleteChildren: forceCleanSlate,
      normalizedOptionIds,
      removedOptionIds: [],
      addedOptionIds: [],
      valueTypedUpdate: { kind: "set", value: normalizedValueTyped },
    };
  }

  const removedOptionIds = getRemovedOptionIds({
    currentOptionIds,
    nextOptionIds: normalizedOptionIds,
  });
  const addedOptionIds = getAddedOptionIds({
    currentOptionIds,
    nextOptionIds: normalizedOptionIds,
  });

  const willDeleteSelf = normalizedOptionIds.length === 0;

  if (willDeleteSelf) {
    return {
      kind: "delete",
      willDeleteChildren: true,
    };
  }

  return {
    kind: "update",
    willDeleteChildren: forceCleanSlate || removedOptionIds.length > 0,
    normalizedOptionIds,
    removedOptionIds,
    addedOptionIds,
    valueTypedUpdate:
      normalizedValueTyped === null
        ? { kind: "leave" }
        : { kind: "set", value: normalizedValueTyped },
  };
}

function isTextLikeQuestionType(
  questionType: string | null | undefined,
): boolean {
  return (
    questionType === TEXT_LIKE_QUESTION_TYPES[0] ||
    questionType === TEXT_LIKE_QUESTION_TYPES[1]
  );
}

export function normalizeValueTyped(valueTyped: string | null | undefined): {
  normalizedValueTyped: string | null;
} {
  if (valueTyped == null) return { normalizedValueTyped: null };
  const trimmed = valueTyped.trim();
  return { normalizedValueTyped: trimmed.length > 0 ? trimmed : null };
}

export function dedupePreserveOrder(ids: readonly string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  ids.forEach((id) => {
    if (seen.has(id)) return;
    seen.add(id);
    result.push(id);
  });
  return result;
}

function getRemovedOptionIds(params: {
  currentOptionIds: readonly string[];
  nextOptionIds: readonly string[];
}): string[] {
  const { currentOptionIds, nextOptionIds } = params;
  if (currentOptionIds.length === 0) return [];

  // Keep stable ordering (based on current ids) for predictability.
  return currentOptionIds.filter((id) => !nextOptionIds.includes(id));
}

function getAddedOptionIds(params: {
  currentOptionIds: readonly string[];
  nextOptionIds: readonly string[];
}): string[] {
  const { currentOptionIds, nextOptionIds } = params;
  if (nextOptionIds.length === 0) return [];

  return nextOptionIds.filter((id) => !currentOptionIds.includes(id));
}
